/**
 * GenerationProfileOverlay — the generation-profile "explore" popup.
 *
 * Replaces the old standalone generation-profile card. The generation anchor in
 * the SummaryHero carries a small trigger; this renders the detail as a full
 * overlay (fixed inset-0), modeled on the shared "Explore hourly" pattern
 * (features/report/TimeseriesExplorer) so it never sits inline in a flex row —
 * an inline panel there pushes the page sideways and leaves charts 0-width.
 *
 * Generation is the fixed physical driver — never banded — so the chart is a
 * plain daily area/line in the `.rd` palette (same data path the card used:
 * hourly_impacts rolled up per day).
 */

import { useMemo } from "react";

export interface GenerationProfileOverlayProps {
  open: boolean;
  onClose: () => void;
  daily?: { date: string; mwh: number }[];
  loading?: boolean;
  annualText: string;
  cfText: string;
  hoursText: string;
}

/** Build an SVG area/line path from the daily series over a fixed viewBox. */
function buildShape(
  pts: { date: string; mwh: number }[],
  W: number,
  H: number,
  PAD: number,
): { line: string; area: string } | null {
  if (pts.length === 0) return null;
  const max = Math.max(...pts.map((p) => p.mwh), 1e-9);
  const n = pts.length;
  const line = pts
    .map((p, i) => {
      const x = n === 1 ? W / 2 : (i / (n - 1)) * W;
      const y = H - PAD - (p.mwh / max) * (H - PAD * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return { line, area: `${line} L${W},${H} L0,${H} Z` };
}

export function GenerationProfileOverlay({
  open,
  onClose,
  daily,
  loading,
  annualText,
  cfText,
  hoursText,
}: GenerationProfileOverlayProps) {
  const shape = useMemo(
    () => buildShape(daily ?? [], 720, 240, 6),
    [daily],
  );

  if (!open) return null;

  return (
    <div
      className="rd-ovl"
      role="dialog"
      aria-modal="true"
      aria-label="Generation profile"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card ovl-panel">
        <div className="ch">
          <h2>Generation profile</h2>
          <button type="button" className="ovl-close" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="cb">
          <div className="statrow">
            <div className="s">
              <div className="n">{annualText}</div>
              <div className="k">annual</div>
            </div>
            <div className="s">
              <div className="n">{cfText}</div>
              <div className="k">capacity factor</div>
            </div>
            <div className="s">
              <div className="n">{hoursText}</div>
              <div className="k">hours modeled</div>
            </div>
          </div>

          <div className="genk">Daily generation</div>
          {loading ? (
            <div className="genempty">Loading generation shape…</div>
          ) : shape ? (
            <svg
              className="spark-lg"
              viewBox="0 0 720 240"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="rd-sg-lg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="var(--accent)" stopOpacity=".35" />
                  <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={shape.area} fill="url(#rd-sg-lg)" />
              <path
                d={shape.line}
                fill="none"
                stroke="var(--accent)"
                strokeWidth={1.6}
              />
            </svg>
          ) : (
            <div className="genempty">No daily generation data.</div>
          )}

          <div className="mut" style={{ fontSize: 11, marginTop: 8 }}>
            Modeled daily generation · the range never touches this — it's the
            physical driver.
          </div>
        </div>
      </div>
    </div>
  );
}
